import Image from "next/image";

import cn from "classnames";

import { urlFor } from "@/sanity/utils";

import type { ProductTileProduct } from "./productTile.component";

export const ProductTileImages = ({
  product,
  selectedVariant,
  pageType = "",
}: ProductTileImagesProps) => {
  if (!product?.featuredImages?.length) return null;

  let images = getTileImages(product.featuredImages, pageType);

  const variantOption = product?.options?.find(
    (option) => option.variant === "color",
  );

  const selectedValue = (variantOption?.optionValues ?? []).find(
    (variant) => (variant as OptionValueWithProduct).slug === selectedVariant,
  ) as OptionValueWithProduct | undefined;
  const selectedVariantImage = selectedValue?.product?.featuredImages;

  // If a variant is selected, use the variant images
  if (selectedVariantImage?.length) {
    images = getTileImages(selectedVariantImage, pageType);
  }

  const hasTwoImages = images?.length === 2;
  return (
    <div className="group relative aspect-square w-full overflow-hidden">
      {images.map((productImage, index) => {
        const url = urlFor(productImage.image).width(600).height(600).url();
        const isFirst = index === 0;
        const isSecond = index === 1;
        return (
          <Image
            key={index}
            src={url}
            alt={productImage?.alt ?? `Product image ${index + 1}`}
            width={600}
            height={600}
            data-testid="product-tile-image"
            className={cn("absolute inset-0 mx-auto object-cover", {
              "px-8 pt-12 pb-4 opacity-100 transition-opacity duration-300 group-hover:opacity-0":
                hasTwoImages && isFirst,
              "opacity-0 transition-opacity duration-300 group-hover:opacity-100":
                hasTwoImages && isSecond,
            })}
          />
        );
      })}
    </div>
  );
};

export type ProductTileImagesProps = {
  selectedVariant: string;
  product?: ProductTileProduct;
  pageType?: string;
};

type OptionValueWithProduct = {
  slug?: string | null;
  product?: {
    featuredImages?: ProductTileProduct["featuredImages"];
  };
};

const getTileImages = (
  featuredImages: ProductTileProduct["featuredImages"],
  pageType: string,
) => {
  if (!featuredImages?.length) return [];

  const pageTypeImages = pageType
    ? featuredImages.filter((image) => image.pageType === pageType)
    : [];

  return (pageTypeImages.length ? pageTypeImages : featuredImages).slice(0, 2);
};
